import { trackVisualEditAction } from 'analytics';
import Icon, { IconType } from 'components/Icon';
import CollapsibleSection from 'components/Widget/CollapsibleSection';
import { Tooltip } from 'components/Widget/Tooltip';
import { SelectionInfo } from 'dragonmouse-shared/devigner';
import { ChatStatus, DeleteVisualEdit, Frame } from 'dragonmouse-shared/interfaces';
import eventManager from 'events/EventManager';
import { useEffect, useRef, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { selectActiveFrameChat } from 'redux/db/reducers';
import { setSelectedDomElements } from 'redux/mode/actions';
import { RootReducer } from 'redux/reducers';
import { getElementName } from 'utils';
import { pushEditToIframe, saveEditToFrame } from 'visualEdits';
import Comments from '../Comments';
import ColorEditControl from './ColorEditControl';
import ImageEditControl from './ImageEditControl';
import OffsetControl from './OffsetControl';
import PaddingEditControl from './PaddingEditControl';
import TextEditControl from './TextEditControl';
import TypographyEditControl from './TypographyEditControl';
import * as S from './styles';

interface ElementInfoProps {
	frame: Frame;
	selectionInfo: SelectionInfo;
}

const ElementInfo = ({ frame, selectionInfo }: ElementInfoProps) => {
	const dispatch = useDispatch();
	const activeFrameChat = useSelector(
		(r: RootReducer) => selectActiveFrameChat(r.db),
		shallowEqual
	);
	const isChatProcessing =
		activeFrameChat?.metadata?.status === ChatStatus.PROCESSING ||
		activeFrameChat?.metadata?.status === ChatStatus.STOPPABLE;

	// Close the right panel when chat finishes processing — the AI edit
	// likely replaced the DOM so the previously selected element no longer exists.
	const wasProcessing = useRef(false);
	useEffect(() => {
		if (wasProcessing.current && !isChatProcessing) {
			dispatch(setSelectedDomElements([]));
			eventManager.emit('devigner-clear-selection', undefined);
		}
		wasProcessing.current = isChatProcessing;
	}, [isChatProcessing, dispatch]);

	const [textCollapsed, setTextCollapsed] = useState(false);
	const [styleCollapsed, setStyleCollapsed] = useState(false);
	const [commentsCollapsed, setCommentsCollapsed] = useState(false);

	const elementName = getElementName(selectionInfo.element);
	const isDesignSystemElement = selectionInfo.element.tagName.toLowerCase().startsWith('ds-');
	const dsTagName = isDesignSystemElement
		? `<${selectionInfo.element.tagName.toLowerCase()}>`
		: null;
	const elementId = selectionInfo.dataset?.mcElementId ?? selectionInfo.element.id;
	const isImage = !!selectionInfo.imageUrl;
	const hasTextNodes = !!selectionInfo.textNodes && selectionInfo.textNodes.length > 0;
	// Visual edits key on elementId — without one we can't persist a delete.
	const canDelete = !!elementId && !!frame.siteId;

	const onDelete = async () => {
		if (!canDelete || !frame.siteId) return;
		const deleteEdit: DeleteVisualEdit = { kind: 'delete', elementId };
		trackVisualEditAction('delete_element');
		// Await: pushEditToIframe must follow persistence so the iframe sees the
		// committed entry on its next read (matches the image-edit pattern).
		await saveEditToFrame(
			frame.id,
			deleteEdit,
			(edit) => edit.kind === 'delete' && edit.elementId === elementId,
			frame.siteId
		);
		pushEditToIframe(frame.id, deleteEdit);
		// Clear both sides of selection: studio's panel state and devigner's
		// in-iframe selection rect.
		dispatch(setSelectedDomElements([]));
		eventManager.emit('devigner-clear-selection', undefined);
	};

	return (
		<S.Container>
			<S.HeaderBase>
				<S.TitleBlock>
					<S.ElementName>{elementName}</S.ElementName>
					{dsTagName && <S.DsComponentName>{dsTagName}</S.DsComponentName>}
				</S.TitleBlock>
				<Tooltip placement="bottom" id="delete-element-tooltip" />
				<S.DeleteButton
					onClick={onDelete}
					disabled={!canDelete || isChatProcessing}
					data-tooltip-id={
						isChatProcessing ? 'visual-edit-disabled-tooltip' : 'delete-element-tooltip'
					}
					data-tooltip-content={
						isChatProcessing
							? 'Visual edit is disabled while chat is processing'
							: 'Delete element (Del)'
					}
				>
					<Icon type={IconType.TRASH} width={16} height={16} />
				</S.DeleteButton>
			</S.HeaderBase>

			<S.EditToolsWrapper $disabled={isChatProcessing}>
				{isChatProcessing && (
					<S.DisabledOverlay
						data-tooltip-id="visual-edit-disabled-tooltip"
						data-tooltip-content="Visual edit is disabled while chat is processing"
					/>
				)}
				{hasTextNodes && (
					<CollapsibleSection
						label="Text"
						isCollapsed={textCollapsed}
						onToggle={() => setTextCollapsed((v) => !v)}
					>
						{/* key={elementId} remounts on selection change so any internal state
						    (e.g. show-all expansion) resets for the new element. */}
						<S.SectionWrapper key={elementId}>
							<TextEditControl frame={frame} selectionInfo={selectionInfo} />
						</S.SectionWrapper>
					</CollapsibleSection>
				)}

				<CollapsibleSection
					label="Style"
					isCollapsed={styleCollapsed}
					onToggle={() => setStyleCollapsed((v) => !v)}
				>
					<S.SectionWrapper key={elementId}>
						{isImage && <ImageEditControl frame={frame} selectionInfo={selectionInfo} />}
						<ColorEditControl frame={frame} selectionInfo={selectionInfo} />
						<TypographyEditControl frame={frame} selectionInfo={selectionInfo} />
						<PaddingEditControl frame={frame} selectionInfo={selectionInfo} />
						<OffsetControl frame={frame} selectionInfo={selectionInfo} />
					</S.SectionWrapper>
				</CollapsibleSection>
			</S.EditToolsWrapper>

			<Tooltip placement="bottom" id="visual-edit-disabled-tooltip" />

			<CollapsibleSection
				label="Comments"
				isCollapsed={commentsCollapsed}
				onToggle={() => setCommentsCollapsed((v) => !v)}
			>
				<S.CommentsWrapper>
					<Comments frame={frame} filterElementId={elementId} />
				</S.CommentsWrapper>
			</CollapsibleSection>
		</S.Container>
	);
};

export default ElementInfo;
